
import BaseFilter from '@/components/filters/Filter';
import { Option } from '@/components/SelectPicker.vue';
import {FilterDateValue as Value, FilterDateMetadata as Metadata, FilterDateValue, DateUtils} from './filterValueFunctions';


export default BaseFilter.extend({
	props: {
		value: {
			type: Object as () => FilterDateValue, // comes in as all empty strings: see decodeInitialState in filterValueFunctions.ts
			required: true,
			default: (): Value&{isDefaultValue: boolean} => ({
				startDate: {
					y: '',
					m: '',
					d: ''
				},
				endDate: {
					y: '',
					m: '',
					d: ''
				},
				mode: 'strict',
				/**
				 * props.definition.metadata can contain a default value.
				 * props.value is our actual value we pass to the calendar, on first setup this is undefined, so we set a default here in this object.
				 *
				 * We want to apply the defaults from definition.metadata to our default value object, but we can't do that,
				 * because we can't access the other props here (as the component hasn't been fully created yet).
				 *
				 * So what we do:
				 * Mark the default value with this boolean
				 * Instead of putting this into our calendar directly, pass it through computedValue() first
				 * There, see if we have this boolean, and if so, replace the two defaults defined above with those from defintion.metadata.
				 *
				 * Then when the user interacts with the calender (i.e. overwrites the default value) this boolean will disappear, and the user dates will be used.
				 * Conveniently this system also lets us detect when only the strict/permissive toggle has been changed, but not the date.
				 */
				isDefaultValue: true
			})
		},
	},

	computed: {
		model(): FilterDateValue {
			return this.value;
		},
		metadata(): Metadata {
			return this.definition.metadata || {
				field: this.id,
				range: false,
			}
		},
		// This can probably be a little simpler, but whatever.
		minDate(): FilterDateValue['startDate']|null { return DateUtils.normalizeBoundaryDate(this.metadata.min); },
		maxDate(): FilterDateValue['startDate']|null { return DateUtils.normalizeBoundaryDate(this.metadata.max); },
		minDateDisplay(): string|null { return this.minDate ? DateUtils.luceneToDisplayString(DateUtils.dateValueToLucene(this.minDate, 'start')) : null;  },
		maxDateDisplay(): string|null { return this.maxDate ? DateUtils.luceneToDisplayString(DateUtils.dateValueToLucene(this.maxDate, 'end')) : null;  },
		minYear(): string|undefined { return this.minDate ? this.minDate.y : undefined; },
		maxYear(): string|undefined { return this.maxDate ? this.maxDate.y : undefined; },
		startMonthLength(): string { return DateUtils.dateValueToLucene({...this.model.startDate, d: ''}, 'end')!.substring(6, 8); },
		endMonthLength(): string { return DateUtils.dateValueToLucene({...this.model.endDate, d: ''}, 'end')!.substring(6, 8); },

		modes(): Option[] {
			return [{
				value: 'permissive',
				label: this.$t('filter.range.permissive').toString(),
				title: this.$t('filter.range.permissiveDescription').toString()
			}, {
				value: 'strict',
				label: this.$t('filter.range.strict').toString(),
				title: this.$t('filter.range.strictDescription').toString()
			}]
		},

		yearFrom: {
			get(): string { return this.model.startDate.y },
			set(y: string) { this.e_input({...this.model, startDate: {...this.model.startDate, y}, isDefaultValue: false})}
		},
		monthFrom: {
			get(): string { return this.model.startDate.m },
			set(m: string) { this.e_input({...this.model, startDate: {...this.model.startDate, m}, isDefaultValue: false})}
		},
		dayFrom: {
			get(): string { return this.model.startDate.d },
			set(d: string) { this.e_input({...this.model, startDate: {...this.model.startDate, d}, isDefaultValue: false})}
		},
		yearTo: {
			get(): string { return this.model.endDate.y },
			set(y: string) { this.e_input({...this.model, endDate: {...this.model.endDate, y}, isDefaultValue: false})}
		},
		monthTo: {
			get(): string { return this.model.endDate.m },
			set(m: string) { this.e_input({...this.model, endDate: {...this.model.endDate, m}, isDefaultValue: false})}
		},
		dayTo: {
			get(): string { return this.model.endDate.d },
			set(d: string) { this.e_input({...this.model, endDate: {...this.model.endDate, d}, isDefaultValue: false})}
		},
	},
});

